import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, RefreshCw, Check, AlertTriangle, X } from "lucide-react";

interface OldDecision {
  query: string;
  winnerName: string;
  winnerPrice: string;
  winnerLabel: string;
  confidence: string;
  decidedAt: string;
}

interface Props {
  decision: OldDecision;
  onClose: () => void;
  onRerun: (query: string) => void;
}

export default function DecisionCompare({ decision, onClose, onRerun }: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-border/50 bg-card p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-display font-bold text-foreground">Compare to now</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="rounded-xl border border-border/40 bg-muted/10 p-3.5 mb-3">
          <p className="text-[10px] text-muted-foreground/60 font-body uppercase tracking-wider mb-1">Your decision</p>
          <p className="text-sm font-display font-semibold text-foreground">{decision.winnerName}</p>
          <div className="flex items-center gap-2 mt-1 text-[11px] font-body text-muted-foreground">
            <span>{decision.winnerPrice}</span>
            <span className="capitalize">{decision.confidence}</span>
            <span>{decision.winnerLabel}</span>
          </div>
          <p className="text-[9px] text-muted-foreground/50 font-body mt-1">
            Decided {new Date(decision.decidedAt).toLocaleDateString()}
          </p>
        </div>

        <div className="flex items-center justify-center py-2">
          <ArrowRight className="w-4 h-4 text-muted-foreground/40" />
        </div>

        <p className="text-[11px] text-muted-foreground font-body text-center mb-3">
          Re-run this query to see if the market has changed.
        </p>

        <button
          onClick={() => {
            onRerun(decision.query);
            onClose();
          }}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-body font-medium hover:opacity-90 transition-opacity"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Compare to current market
        </button>
      </div>
    </motion.div>
  );
}
